import { z } from 'zod';
import { idSchema, isoDateTime, platformSchema } from './common.js';
export const postStatusSchema = z.enum([
    'draft',
    'pending',
    'approved',
    'rejected',
    'scheduled',
    'publishing',
    'published',
    'failed',
]);
export const postTypeSchema = z.enum([
    'text',
    'single-image',
    'multi-image',
    'carousel',
    'article',
    'thread',
    'video',
    'reels',
    'feed-single',
    'feed-carousel',
    'stories',
    'short',
    'pin',
    'link',
    'text-image',
    'document', // linkedin PDF document post (swipeable slides)
    'gif', // x/twitter animated GIF (single media)
    'poll', // x/twitter poll (2–4 options + duration)
    // platform-specific extras (composer fidelity)
    'photo', // tiktok photo carousel
    'live', // tiktok / youtube live
    'community', // youtube community post
    'image', // threads image post
    'video-pin', // pinterest video pin
    'idea-pin', // pinterest idea pin
]);
/**
 * Per-platform composer rules — the single source of truth consumed by the web
 * composer (post-type tabs), the AI gateway, and any publish-time validation.
 * UI presentation (icons/labels) stays in the web; only the *rules* live here so
 * the 8 platforms stay consistent with the backend enums.
 */
export const postTypesByPlatform = {
    linkedin: ['text', 'single-image', 'multi-image', 'document', 'video', 'link'],
    twitter: ['text', 'single-image', 'multi-image', 'video', 'gif', 'poll', 'link', 'thread'],
    facebook: ['text', 'single-image', 'multi-image', 'link'],
    instagram: ['feed-single', 'feed-carousel', 'reels', 'stories'],
    tiktok: ['video', 'photo', 'live'],
    youtube: ['video', 'short', 'community'],
    threads: ['text', 'image', 'video'],
    pinterest: ['pin', 'video-pin', 'idea-pin'],
};
/** Default post type selected when a platform tab is first opened. */
export const defaultPostType = {
    linkedin: 'single-image',
    twitter: 'text',
    facebook: 'single-image',
    instagram: 'feed-single',
    tiktok: 'video',
    youtube: 'short',
    threads: 'text',
    pinterest: 'pin',
};
/** Platforms that cannot publish without at least one media attachment. */
export const requiredMedia = {
    linkedin: false,
    twitter: false,
    facebook: false,
    instagram: true,
    tiktok: true,
    youtube: true,
    threads: false,
    pinterest: true,
};
// ─── LinkedIn publish validation (DEV-LINKEDIN §3.2) ───────────────────
// Single source of truth shared by the composer (panel) and the publish worker
// (server-side re-check so a tampered client can't push an invalid post).
export const LINKEDIN_CAPTION_MAX = 3000;
/** Returns blocking + warning checks. Empty `blocking` ⇒ publishable. */
export function validateLinkedInPost(input) {
    const checks = [];
    const cap = input.caption ?? '';
    const n = input.assetCount ?? 0;
    if (cap.length > LINKEDIN_CAPTION_MAX) {
        checks.push({ code: 'caption_length', severity: 'blocking', message: `Caption is ${cap.length} / ${LINKEDIN_CAPTION_MAX} — over by ${cap.length - LINKEDIN_CAPTION_MAX}.` });
    }
    switch (input.contentType) {
        case 'multi-image':
            if (n < 2 || n > 9)
                checks.push({ code: 'multi_image_count', severity: 'blocking', message: `Multi-image needs 2–9 images (have ${n}).` });
            break;
        case 'single-image':
        case 'video':
        case 'document':
            if (n !== 1)
                checks.push({ code: 'asset_count', severity: 'blocking', message: `${input.contentType} needs exactly 1 asset (have ${n}).` });
            break;
        case 'link':
            if (!input.linkUrl || !/^https?:\/\/\S+\.\S+/.test(input.linkUrl.trim())) {
                checks.push({ code: 'link_url', severity: 'blocking', message: 'Link share needs a valid URL.' });
            }
            if (n > 0)
                checks.push({ code: 'unexpected_media', severity: 'warning', message: 'Link posts use the auto-fetched preview; attached media is ignored.' });
            break;
        case 'text':
            if (n > 0)
                checks.push({ code: 'unexpected_media', severity: 'warning', message: 'Text posts carry no media — switch type to attach images.' });
            break;
    }
    const tags = (cap.match(/#\w+/g) ?? []).length;
    if (tags > 5) {
        checks.push({ code: 'hashtags', severity: 'warning', message: `${tags} hashtags. On LinkedIn hashtags are a weak search/discovery signal (not a reach multiplier since 2023) — a few specific tags is plenty.` });
    }
    return checks;
}
// ─── X / Twitter publish validation (DEV-X §4.1, §5) ───────────────────
// Single source of truth shared by the composer panel and the publish worker.
// X's caption cap is account-dependent (280 vs long-post), so it is passed in
// per-connection rather than hard-coded.
export const X_CAPTION_MAX = 280;
export const X_LONG_CAPTION_MAX = 25_000; // verified/premium long posts
export const X_MAX_IMAGES = 4;
export const X_POLL_MIN_OPTIONS = 2;
export const X_POLL_MAX_OPTIONS = 4;
export const X_POLL_OPTION_MAX = 25; // chars per option
export const X_POLL_MIN_DURATION_MIN = 5; // 5 minutes
export const X_POLL_MAX_DURATION_MIN = 10_080; // 7 days
/** Effective caption cap for the connected account. */
export function xCaptionMax(longPosts) {
    return longPosts ? X_LONG_CAPTION_MAX : X_CAPTION_MAX;
}
/** Media rules for a single X post unit (a standalone post or one thread segment). */
function checkXMedia(imageCount, videoCount, gifCount, prefix = '') {
    const out = [];
    if (imageCount > X_MAX_IMAGES)
        out.push({ code: 'image_count', severity: 'blocking', message: `${prefix}Up to ${X_MAX_IMAGES} images per post (have ${imageCount}).` });
    if (videoCount > 1)
        out.push({ code: 'video_count', severity: 'blocking', message: `${prefix}Only 1 video per post (have ${videoCount}).` });
    if (gifCount > 1)
        out.push({ code: 'gif_count', severity: 'blocking', message: `${prefix}Only 1 GIF per post (have ${gifCount}).` });
    const kinds = (imageCount > 0 ? 1 : 0) + (videoCount > 0 ? 1 : 0) + (gifCount > 0 ? 1 : 0);
    if (kinds > 1)
        out.push({ code: 'mixed_media', severity: 'blocking', message: `${prefix}Can't mix images, video, and GIF in one post.` });
    return out;
}
/** Returns blocking + warning + info checks. Empty `blocking` ⇒ publishable. */
export function validateXPost(input) {
    const checks = [];
    const cap = xCaptionMax(input.longPosts);
    // Tier gate (§3) — applies to every X publish.
    if (input.tierConfigured === false) {
        checks.push({ code: 'tier_required', severity: 'blocking', message: 'X publishing needs a paid API tier on this connection.' });
    }
    if (input.contentType === 'thread') {
        const segs = input.segments ?? [];
        if (segs.length < 1) {
            checks.push({ code: 'thread_empty', severity: 'blocking', message: 'A thread needs at least one segment.' });
        }
        segs.forEach((s, i) => {
            const n = (s.text ?? '').length;
            if (n === 0 && s.imageCount + s.videoCount + s.gifCount === 0) {
                checks.push({ code: 'segment_empty', severity: 'blocking', message: `Segment ${i + 1} is empty.` });
            }
            if (n > cap)
                checks.push({ code: 'segment_length', severity: 'blocking', message: `Segment ${i + 1} is ${n} / ${cap}.` });
            checks.push(...checkXMedia(s.imageCount, s.videoCount, s.gifCount, `Segment ${i + 1}: `));
        });
        return checks;
    }
    // Single-post units.
    const len = (input.caption ?? '').length;
    if (len > cap)
        checks.push({ code: 'caption_length', severity: 'blocking', message: `Caption is ${len} / ${cap} — over by ${len - cap}.` });
    if (input.contentType === 'poll') {
        const opts = (input.poll?.options ?? []).map((o) => o.trim()).filter(Boolean);
        if (opts.length < X_POLL_MIN_OPTIONS || opts.length > X_POLL_MAX_OPTIONS) {
            checks.push({ code: 'poll_options', severity: 'blocking', message: `Poll needs ${X_POLL_MIN_OPTIONS}–${X_POLL_MAX_OPTIONS} options (have ${opts.length}).` });
        }
        if (opts.some((o) => o.length > X_POLL_OPTION_MAX)) {
            checks.push({ code: 'poll_option_length', severity: 'blocking', message: `Each poll option must be ≤ ${X_POLL_OPTION_MAX} characters.` });
        }
        const dur = input.poll?.durationMinutes ?? 0;
        if (dur < X_POLL_MIN_DURATION_MIN || dur > X_POLL_MAX_DURATION_MIN) {
            checks.push({ code: 'poll_duration', severity: 'blocking', message: `Poll duration must be ${X_POLL_MIN_DURATION_MIN} min – 7 days.` });
        }
        if (input.imageCount + input.videoCount + input.gifCount > 0) {
            checks.push({ code: 'poll_media', severity: 'warning', message: 'Polls cannot carry media — it will be ignored.' });
        }
    }
    else {
        checks.push(...checkXMedia(input.imageCount, input.videoCount, input.gifCount));
    }
    if (input.contentType === 'link') {
        checks.push({ code: 'link_autocard', severity: 'info', message: 'X auto-generates the link preview card — no separate field needed.' });
    }
    return checks;
}
export const draftSchema = z.object({
    id: idSchema,
    project_id: idSchema,
    campaign_id: idSchema.nullable(),
    connection_id: idSchema,
    platform: platformSchema,
    post_type: postTypeSchema,
    caption: z.string(),
    hashtags: z.array(z.string()).default([]),
    mentions: z.array(z.string()).default([]),
    status: postStatusSchema,
    scheduled_at: isoDateTime.nullable(),
    published_at: isoDateTime.nullable(),
    external_post_id: z.string().nullable(),
    external_url: z.string().url().nullable(),
    char_count: z.number().int().nonnegative(),
    char_limit: z.number().int().positive(),
    ai_generated: z.boolean(),
    created_by: idSchema,
    created_at: isoDateTime,
    updated_at: isoDateTime,
});
export const createDraftInput = draftSchema
    .pick({
    project_id: true,
    connection_id: true,
    platform: true,
    post_type: true,
    caption: true,
    hashtags: true,
    mentions: true,
})
    .extend({
    media_ids: z.array(idSchema).default([]),
});
export const updateDraftInput = createDraftInput.partial();
export const scheduleInput = z.object({
    when: isoDateTime,
});
export const requestChangesInput = z.object({
    comment: z.string().min(1).max(2000),
});
export const commentSchema = z.object({
    id: idSchema,
    post_id: idSchema,
    author_id: idSchema,
    body: z.string(),
    kind: z.enum(['comment', 'change_request', 'approval']),
    created_at: isoDateTime,
});
export const activityKind = z.enum([
    'created',
    'edited',
    'validator_run',
    'comment',
    'change_request',
    'approval',
    'scheduled',
    'publishing',
    'published',
    'failed',
    'retried',
    'unscheduled',
]);
export const activitySchema = z.object({
    id: idSchema,
    post_id: idSchema,
    actor_id: idSchema.nullable(),
    kind: activityKind,
    payload: z.record(z.unknown()).default({}),
    created_at: isoDateTime,
});
//# sourceMappingURL=draft.js.map